import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Helper,
  MyOrganizationFacade,
  SharedFacade,
  customDecode,
  noWhitespaceValidator,
} from '@piqet-angular/data';
import { AutomationStatusEnum, CustomModalService, emailRegex } from '@piqet-angular/shared';
import { environment } from 'apps/piqet-angular/src/environments/environment';
import { MustMatch } from '../change-password/must-match.validator';
import { TitleCasePipe } from '@angular/common';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { AdministratorService } from 'libs/data/src/lib/services/administrator/administrator.service';
import { MyOrganizationService } from 'libs/data/src/lib/services/my-organization/my-organization.service';
import { ManageUsersDataGridService } from '@piqet-angular/features';

@Component({
  selector: 'piqet-angular-new-my-organization-manage-user',
  templateUrl: './new-my-organization-manage-user.component.html',
  styleUrls: ['./new-my-organization-manage-user.component.scss'],
  providers: [TitleCasePipe],
})
export class NewMyOrganizationManageUserComponent {
  // Input properties for standalone mode
  @Input() isStandaloneMode: boolean = false;
  @Input() customData: any;
  @Input() modalLabel: any;
  @Input() organizationId: any;
  @Input() hideOrganizationField: boolean = false;
  @Input() hideRoleField: boolean = false;
  @Input() hideTriggerAutomation: boolean = false;
  @Input() isEdit: any;

  // Output events
  @Output() saveManageUserEvent = new EventEmitter<{
    payload: any;
    pathName: any;
  }>();
  @Output() cancelEvent = new EventEmitter<void>();

  countries: any;
  energyItems: any;
  roleAccesslist: any;
  organizationlist: any;
  submitted = false;
  isOrgDetail: any;
  selectedCountry: any;
  manageUserForm!: FormGroup;
  selectedState: any;
  selectedOrg: any;
  isFromAdminOrg: boolean = false;
  activatedRoute: any;
  isEmailValid: boolean = false;
  organizationSelected = true;
  isReadOnly!: boolean;
  orgName: any;
  activeCheckbox = true;
  currentEmail: string = '';
  isEmailValidationPending: boolean = true;
  lastValidatedEmail: string | null = null;
  isShowInput = false;
  customStorageId: any;
  fieldTextTypeNewPwd = false;
  fieldTextTypeConfirmPwd = false;
  specialCharactersflag: boolean = true;
  upperCaseCharactersflag: boolean = true;
  lowerCaseCharactersflag: boolean = true;
  numberCharactersflag: boolean = true;
  minLengthsflag: boolean = true;
  stateList: any;
  emailExist: boolean = false;
  selectedRole: any;
  orgId: any;
  modalName = 'Update user';
  showEdit: boolean = false;
  showCreate: boolean = false;
  showView: boolean = false;
  checkEntireAccess: boolean = false;
  automationList: any;
  triggerAutomation: any = false;
  hasAutomationInProgress: boolean = false;
  automationInProgressNames: string = '';
  @ViewChild('emailInput') emailInput!: ElementRef<HTMLInputElement>;

  constructor(
    private modal: CustomModalService,
    private manageUsersDataGridService: ManageUsersDataGridService,
    public sharedFacade: SharedFacade,
    private formBuilder: FormBuilder,
    public route: ActivatedRoute,
    private titleCasePipe: TitleCasePipe,
    private sharedService: SharedService,
    private router: Router,
    private organizationPreference: MyOrganizationService,
    private administratorService: AdministratorService
  ) {
    this.activatedRoute = this.router.routerState.snapshot.url;
  }

  ngOnInit() {
    // Initialize component based on mode (standalone or modal)
    if (this.isStandaloneMode) {
      this.initializeStandaloneMode();
    } else {
      this.initializeModalMode();
    }

    this.sharedFacade.clearEmailValidation();
    this.setupForm();
    this.setupAccessPermissions();
  }

  private initializeStandaloneMode() {
    // In standalone mode, data comes from @Input properties
    this.isReadOnly = false;
    this.isOrgDetail = false;
    this.countries = this.customData?.country || [];
    this.roleAccesslist = this.customData?.roleAccesslist || [];
    this.organizationlist = this.customData?.organizationlistItems || [];
    this.automationList = this.customData?.automationList || [];

    // Set default values for standalone mode
    this.isShowInput = this.modalLabel !== this.modalName;
    this.isEmailValidationPending = this.modalLabel !== this.modalName;
    this.activeCheckbox = true;

    // Apply field hiding based on input properties
    if (this.hideOrganizationField) {
      this.organizationSelected = false;
    }

    // Set a default role if roles are hidden
    if (this.hideRoleField && this.roleAccesslist?.length > 0) {
      const defaultRole =
        this.roleAccesslist.find(
          (r: any) => r.name.toLowerCase() === 'member' || r.name.toLowerCase() === 'user'
        ) || this.roleAccesslist[0];
      this.selectedRole = defaultRole;
    }
  }

  private initializeModalMode() {
    // Original modal initialization code
    this.isReadOnly = this.activatedRoute.includes('AdminManageUsers') ? false : true;
    this.modalLabel = this.modal?.modalService?.config?.initialState?.['label'] || this.modalLabel;
    this.orgName = this.modal?.modalService?.config?.initialState?.['oName'];
    this.isOrgDetail = this.modal?.modalService?.config?.initialState?.['isOrgDetail'];
    this.customData =
      this.modal?.modalService?.config?.initialState?.['newMyOrganizationManageUserData'] ||
      this.customData;
    this.organizationId =
      this.modal?.modalService?.config?.initialState?.['organizationId'] || this.organizationId;
    this.isEdit = this.modal?.modalService?.config?.initialState?.['isEdit'] || this.isEdit;

    this.isShowInput = this.modalLabel === this.modalName;
    this.isEmailValidationPending = this.modalLabel !== this.modalName;
    this.activeCheckbox = this.modalLabel === this.modalName ? this.customData?.isActive : true;
    this.triggerAutomation = this.hasSelectedAutomation(this.customData?.automationList);

    this.countries = this.customData?.country || [];
    this.roleAccesslist = this.customData?.roleAccesslist || [];
    this.organizationlist = this.customData?.organizationlistItems || [];
    this.automationList = this.customData?.automationList || [];
  }

  private setupForm() {
    this.getQueryParams();
    this.mapCountryList();
    this.mapStateList();
    this.setRoleValue();
    this.checkAutomationInProgress();
    this.customStorageId = this.customData?.id;

    const reg = emailRegex;
    const pwdReg = '^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$';

    this.manageUserForm = this.formBuilder.group(
      {
        firstName: [
          this.customData?.firstname || null,
          [Validators.required, Validators.minLength(2), noWhitespaceValidator],
        ],
        lastName: [
          this.customData.lastname || null,
          [Validators.required, Validators.minLength(2), noWhitespaceValidator],
        ],
        organizationName: [
          this.isOrgDetail
            ? Number(this.organizationId)
            : this.isStandaloneMode
            ? Number(this.organizationId)
            : this.customData?.organizationId,
          this.hideOrganizationField ? [] : [Validators.required],
        ],
        accessRole: [this.selectedRole || null],
        isActive: [this.activeCheckbox],
        email: [
          this.customData.userName || '',
          [
            Validators.required,
            Validators.email,
            Validators.pattern(reg),
            Validators.maxLength(150),
          ],
        ],
        address: [this.customData?.address || ''],
        postCode: [this.customData?.postCode || ''],
        phone: [this.customData?.phone || ''],
        country: [this.selectedCountry || null, Validators.required],
        state: [this.selectedState || null],
        Password: [''],
        ConfirmPassword: [''],
        triggerAutomation: [false],
        automationName: [null],
      },
      {
        validator: MustMatch('Password', 'ConfirmPassword'),
      }
    );

    // Set up password validators
    const shouldRequirePassword = !this.isShowInput || this.isStandaloneMode;
    if (shouldRequirePassword) {
      this.manageUserForm
        .get('Password')
        ?.setValidators([Validators.required, Validators.pattern(pwdReg)]);
      this.manageUserForm.get('ConfirmPassword')?.setValidators([Validators.required]);
    } else {
      this.manageUserForm.get('Password')?.setValidators(null);
      this.manageUserForm.get('ConfirmPassword')?.setValidators(null);
    }

    // Set up role validators
    if (
      !this.hideRoleField &&
      (!this.isShowInput ||
        (this.customData?.isSAdmin && this.isShowInput) ||
        (this.customData?.isAdmin && this.isShowInput))
    ) {
      this.manageUserForm.get('accessRole')?.setValidators([Validators.required]);
    } else {
      this.manageUserForm.get('accessRole')?.setValidators(null);
    }

    this.manageUserForm.get('accessRole')?.updateValueAndValidity();
    this.manageUserForm.get('Password')?.updateValueAndValidity();
    this.manageUserForm.get('ConfirmPassword')?.updateValueAndValidity();

    // Set default values for hidden fields in standalone mode
    if (this.isStandaloneMode) {
      if (this.hideRoleField && this.selectedRole) {
        this.manageUserForm.patchValue({ accessRole: this.selectedRole });
      }
      if (this.hideOrganizationField && this.organizationId) {
        this.manageUserForm.patchValue({ organizationName: Number(this.organizationId) });
      }
    }

    // Subscribe to email changes
    this.manageUserForm.get('email')?.valueChanges.subscribe(value => {
      if (value === '') {
        this.sharedFacade.clearEmailValidation();
      }
    });

    this.updateOrganizationValidators();
  }

  private setupAccessPermissions() {
    if (this.isStandaloneMode) {
      // For standalone mode (create user), always allow create access
      this.showCreate = true;
      this.checkEntireAccess = true;
    } else {
      // Original permission logic for modal mode
      this.showEdit = this.manageUsersDataGridService.isAccess(this.activatedRoute, 'edit');
      this.showCreate = this.manageUsersDataGridService.isAccess(this.activatedRoute, 'create');
      this.showView = this.manageUsersDataGridService.isAccess(this.activatedRoute, 'view');
      this.checkEntireAccess =
        (this.showCreate && !this.isEdit) ||
        (this.isEdit && this.showEdit) ||
        (this.isEdit && !this.showView);
    }
  }

  // Add method to check if a field should be shown
  shouldShowOrganizationField(): boolean {
    if (this.isStandaloneMode && this.hideOrganizationField) {
      return false;
    }
    return (
      this.organizationSelected &&
      ((this.customData?.isSAdmin &&
        !this.activatedRoute?.includes('/my-organization/manageUsers')) ||
        !this.isShowInput)
    );
  }

  shouldShowRoleField(): boolean {
    if (this.isStandaloneMode && this.hideRoleField) {
      return false;
    }
    return (
      !this.isShowInput ||
      (this.customData?.isSAdmin && this.isShowInput) ||
      (this.customData?.isAdmin && this.isShowInput)
    );
  }

  shouldShowTriggerAutomation(): boolean {
    if (this.isStandaloneMode && this.hideTriggerAutomation) {
      return false;
    }
    return this.customData?.isSAdmin;
  }

  getSelectedAutomationId(automationList: any[]): number | null {
    if (!automationList || automationList.length === 0) {
      return null;
    }

    const selectedAutomation = automationList.find(item => item.selected);
    return selectedAutomation ? selectedAutomation.id : null;
  }

  hasSelectedAutomation(automationList: any[]): boolean {
    return automationList?.some(item => item.selected) || false;
  }

  checkAutomationInProgress() {
    const statusMap: { [key: number]: string } = {
      0: 'Pending',
      1: 'In Progress',
      2: 'Completed',
    };

    const activeAutomations =
      this.automationList?.filter(
        (item: { status: number }) =>
          item.status === AutomationStatusEnum.Pending ||
          item.status === AutomationStatusEnum.InProgress
      ) || [];

    if (activeAutomations.length > 0) {
      this.hasAutomationInProgress = true;
      this.automationInProgressNames = activeAutomations
        .map((a: { name: string; status: number }) => `${a.name} (${statusMap[a.status]})`)
        .join(', ');
    } else {
      this.hasAutomationInProgress = false;
      this.automationInProgressNames = '';
    }
  }

  updateOrganizationValidators() {
    if (!this.isFromAdminOrg && !this.hideOrganizationField) {
      this.manageUserForm.controls['organizationName'].setValidators([Validators.required]);
    } else {
      this.manageUserForm.controls['organizationName'].clearValidators();
    }
    this.manageUserForm.controls['organizationName'].updateValueAndValidity();
  }

  getQueryParams() {
    this.route.queryParams.subscribe((data: any) => {
      if (data?.opUser) {
        this.isReadOnly = !!data.opUser;
        this.orgId = atob(decodeURIComponent(customDecode(data.oId)));
        this.isFromAdminOrg = true;
      }
    });
  }

  async onChange(event: any) {
    if (event) {
      this.stateList = await this.GetStateListByCountryId(event);
    }
    this.manageUserForm.controls['state'].setValue(null);
  }

  async setRoleValue() {
    if (this.customData?.roleAccesslist) {
      await this.customData.roleAccesslist.map((item: any) => {
        if (item.idStr == this.customData?.roleId) {
          return (this.selectedRole = item);
        }
      });
    }
  }

  async GetStateListByCountryId(countryId: any) {
    const stateList = await this.sharedService.getStates(countryId);
    const updatedStateData = stateList.data.map((item: any) => {
      return {
        ...item,
        name: Helper.convertToTitleCaseforState(item.name),
      };
    });
    return updatedStateData;
  }

  restrictE(event: any) {
    if (event.key === 'e' || event.key === 'E' || event.key === '-') {
      event.preventDefault();
    }
  }

  restrictSpecial(event: any) {
    var inp = String.fromCharCode(event.keyCode);
    if (/[a-zA-Z0-9]/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  async mapCountryList() {
    if (this.customData?.country) {
      await this.customData.country.map((item: any) => {
        if (item.id == this.customData?.countryId) {
          this.selectedCountry = item;
        }
      });
      if (this.selectedCountry?.id) {
        this.stateList = await this.GetStateListByCountryId(this.selectedCountry.id);
      }
    }
  }

  async mapStateList() {
    if (this.customData?.state) {
      await this.customData.state.map((item: any) => {
        if (item.id == this.customData?.stateId) {
          this.selectedState = item;
        }
      });
    }
  }

  toggleFieldNewPwd() {
    this.fieldTextTypeNewPwd = !this.fieldTextTypeNewPwd;
  }

  toggleFieldConfirmPwd() {
    this.fieldTextTypeConfirmPwd = !this.fieldTextTypeConfirmPwd;
  }

  validationsPassword() {
    const isValidate = Helper.passwordValidatior(this.manageUserForm.controls['Password'].value);
    this.upperCaseCharactersflag = isValidate.upperCaseCharactersflag;
    this.lowerCaseCharactersflag = isValidate.lowerCaseCharactersflag;
    this.numberCharactersflag = isValidate.numberCharactersflag;
    this.specialCharactersflag = isValidate.specialCharactersflag;
    this.minLengthsflag = isValidate.minLengthsflag;
  }

  commonInputSearch(term: string, item: any) {
    return item.name.toLowerCase().includes(term.toLowerCase());
  }

  onClose() {
    if (this.isStandaloneMode) {
      this.cancelEvent.emit();
    } else {
      this.modal.close();
      this.router.navigate([], { queryParams: {} });
    }
  }

  changeEmail(event: any) {
    this.currentEmail = '';
  }

  getAllRolesList(eve?: any) {
    if (!eve?.id) {
      return;
    }
    this.administratorService
      .getAdminAllRoleList(eve.id, this.customData?.userId)
      .subscribe((res: any) => {
        if (res.data.length) {
          this.roleAccesslist = res.data.map((item: any) => ({
            ...item,
            name: item.name,
          }));

          const selectedRoles = this.roleAccesslist.find((role: any) => role.selected);
          this.manageUserForm.patchValue({ accessRole: selectedRoles });
        } else {
          this.manageUserForm.controls['accessRole'].reset();
        }
      });
  }

  getEmailValidation() {
    this.currentEmail = this.manageUserForm.controls['email'].value;
    this.isEmailValidationPending = false;

    if (this.customData?.userName == this.manageUserForm.controls['email'].value) {
      this.isEmailValid = false;
      return;
    }

    if (this.currentEmail === this.lastValidatedEmail) {
      return;
    }

    const payload = {
      id: this.customData?.aspNetUserId ?? 0,
      email: this.manageUserForm.controls['email'].value,
    };

    this.sharedService.isEmailExist(payload).subscribe((data: any) => {
      if (!data.messageType) {
        this.isEmailValid = data.data.value;
        this.lastValidatedEmail = this.currentEmail;
      }
    });
  }

  onSave() {
    this.submitted = true;

    if (this.manageUserForm.invalid || this.isEmailValid || this.isEmailValidationPending) {
      if (this.isEmailValidationPending && !this.isShowInput) {
        this.emailInput?.nativeElement?.focus();
      }
      return;
    }

    // Get the role value (use default if hidden)
    let roleValue = this.manageUserForm.controls['accessRole'].value;
    if (this.hideRoleField && this.selectedRole) {
      roleValue = this.selectedRole;
    }

    // Get the organization value (use input organizationId if hidden)
    let orgValue = this.manageUserForm.controls['organizationName'].value;
    if (this.hideOrganizationField && this.organizationId) {
      orgValue = Number(this.organizationId);
    }

    const data = {
      userId: this.customData?.userId || 0,
      firstname: this.manageUserForm.controls['firstName'].value.trimStart(),
      lastname: this.manageUserForm.controls['lastName'].value.trimStart(),
      phone: this.manageUserForm.controls['phone'].value,
      userName: this.manageUserForm.controls['email'].value.trimStart(),
      address: this.manageUserForm.controls['address'].value.trimStart(),
      postCode: this.manageUserForm.controls['postCode'].value,
      isActive: this.manageUserForm.controls['isActive'].value,
      roleId: roleValue?.idStr || null,
      roleName: roleValue?.name,
      countryId: this.manageUserForm.controls['country'].value?.id,
      Password: this.manageUserForm.controls['Password'].value,
      ConfirmPassword: this.manageUserForm.controls['ConfirmPassword'].value,
      stateId: this.manageUserForm.controls['state'].value?.id || null,
      countryName: this.manageUserForm.controls['country'].value?.name,
      stateName: this.manageUserForm.controls['state'].value?.name,
      organizationId: orgValue || this.customData?.organizationId,
      activationUrl: encodeURIComponent(
        environment.locationPath + `/#/activate-user?activationCode=`
      ),
      isSAdmin: this.customData?.isSAdmin || false,
      automationId: this.manageUserForm.controls['triggerAutomation'].value
        ? this.manageUserForm.controls['automationName'].value
        : 0,
    };

    this.saveManageUserEvent.emit({ payload: data, pathName: this.activatedRoute });
  }
}
